import React from 'react';
import { Box, Container, Typography, Paper, Divider, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY } from '../../theme';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import ShieldIcon from '@mui/icons-material/Shield';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { keyframes } from '@mui/system';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export default function Policy() {
    const { t, i18n } = useTranslation();
    const [language, setLanguage] = React.useState(() => {
        return i18n.language || localStorage.getItem('menuLanguage') || 'en';
    });

    // Sync language state with i18n.language when it changes
    React.useEffect(() => {
        if (i18n.language && i18n.language !== language) {
            setLanguage(i18n.language);
        }
    }, [i18n.language, language]);

    // When user changes language via toggle, update localStorage and i18n
    const handleLanguageChange = React.useCallback((newLang) => {
        setLanguage(newLang);
        localStorage.setItem('menuLanguage', newLang);
        i18n.changeLanguage(newLang);
    }, [i18n]);

    const sections = [
        {
            icon: <VerifiedUserIcon sx={{ fontSize: 48 }} />,
            color: '#4CAF50',
            gradient: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
            key: 'agePolicy'
        },
        {
            icon: <GroupsIcon sx={{ fontSize: 48 }} />,
            color: '#2196F3',
            gradient: 'linear-gradient(135deg, #2196F3 0%, #42A5F5 100%)',
            key: 'atmosphere'
        },
        {
            icon: <SmokingRoomsIcon sx={{ fontSize: 48 }} />,
            color: '#9C27B0',
            gradient: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
            key: 'equipment'
        },
        {
            icon: <AccessTimeIcon sx={{ fontSize: 48 }} />,
            color: '#FF9800',
            gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
            key: 'loungeStandard'
        },
        {
            icon: <RestaurantIcon sx={{ fontSize: 48 }} />,
            color: '#00BCD4',
            gradient: 'linear-gradient(135deg, #00BCD4 0%, #26C6DA 100%)',
            key: 'externalConsumption'
        },
        {
            icon: <LocalBarIcon sx={{ fontSize: 48 }} />,
            color: '#E91E63',
            gradient: 'linear-gradient(135deg, #E91E63 0%, #EC407A 100%)',
            key: 'alcohol'
        },
        {
            icon: <ShieldIcon sx={{ fontSize: 48 }} />,
            color: '#FFC107',
            gradient: 'linear-gradient(135deg, #FFC107 0%, #FFD54F 100%)',
            key: 'personalResponsibility'
        },
        {
            icon: <CameraAltIcon sx={{ fontSize: 48 }} />,
            color: '#3F51B5',
            gradient: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
            key: 'filming'
        },
        {
            icon: <ReportProblemIcon sx={{ fontSize: 48 }} />,
            color: '#F44336',
            gradient: 'linear-gradient(135deg, #F44336 0%, #EF5350 100%)',
            key: 'damageResponsibility'
        }
    ];

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(180deg, #0a0c0d 0%, #1a1d20 100%)',
                pt: { xs: 8, md: 12 },
                pb: { xs: 4, md: 8 },
                px: { xs: 2, sm: 3, md: 0 },
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Animated background circles */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '-10%',
                    right: '-5%',
                    width: { xs: '250px', md: '500px' },
                    height: { xs: '250px', md: '500px' },
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,107,0,0.15) 0%, transparent 70%)',
                    animation: `${float} 6s ease-in-out infinite`,
                    pointerEvents: 'none'
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '-10%',
                    left: '-5%',
                    width: { xs: '300px', md: '600px' },
                    height: { xs: '300px', md: '600px' },
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,159,74,0.1) 0%, transparent 70%)',
                    animation: `${float} 8s ease-in-out infinite`,
                    animationDelay: '1s',
                    pointerEvents: 'none'
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <LanguageSwitcher language={language} setLanguage={handleLanguageChange} style="dark" floating={true} />

                {/* Hero Header */}
                <Box
                    sx={{
                        textAlign: 'center',
                        mb: { xs: 4, md: 8 },
                        animation: `${fadeInUp} 0.8s ease-out`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'inline-block',
                            mb: 3,
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '120%',
                                height: '120%',
                                background: 'radial-gradient(circle, rgba(255,107,0,0.2) 0%, transparent 70%)',
                                borderRadius: '50%',
                                animation: `${float} 4s ease-in-out infinite`,
                            }
                        }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: FONT_FAMILY,
                                background: 'linear-gradient(135deg, #ff6b00 0%, #ff9f4a 50%, #ffd700 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontWeight: 800,
                                fontSize: { xs: '2.5rem', md: '4rem' },
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                position: 'relative',
                                mb: 2
                            }}
                        >
                            {t('policy.brandName')}
                        </Typography>
                    </Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: FONT_FAMILY,
                            color: '#fff',
                            fontWeight: 300,
                            mb: 3,
                            fontSize: { xs: '1.5rem', md: '2rem' },
                            letterSpacing: '1px'
                        }}
                    >
                        {t('policy.title')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1, md: 2 }, mb: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ width: { xs: 30, md: 60 }, height: 2, background: 'linear-gradient(90deg, transparent, #ff6b00, transparent)' }} />
                        <Typography
                            sx={{
                                fontFamily: FONT_FAMILY,
                                color: '#ff9f4a',
                                fontStyle: 'italic',
                                fontSize: { xs: '0.9rem', md: '1.1rem' }
                            }}
                        >
                            {t('policy.subtitle')}
                        </Typography>
                        <Box sx={{ width: { xs: 30, md: 60 }, height: 2, background: 'linear-gradient(90deg, transparent, #ff6b00, transparent)' }} />
                    </Box>
                </Box>

                {/* Introduction Card */}
                <Paper
                    elevation={0}
                    sx={{
                        background: 'rgba(255,255,255,0.03)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: { xs: 3, md: 4 },
                        p: { xs: 3, sm: 4, md: 5 },
                        mb: { xs: 4, md: 6 },
                        border: '1px solid rgba(255,107,0,0.2)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                        animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #ff6b00, #ff9f4a, #ffd700, #ff9f4a, #ff6b00)',
                            backgroundSize: '200% 100%',
                            animation: 'gradient-shift 3s ease infinite',
                            '@keyframes gradient-shift': {
                                '0%, 100%': { backgroundPosition: '0% 50%' },
                                '50%': { backgroundPosition: '100% 50%' }
                            }
                        }
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: FONT_FAMILY,
                            color: '#f0f0f0',
                            fontSize: { xs: '1rem', md: '1.2rem' },
                            lineHeight: { xs: 1.8, md: 2 },
                            mb: 3,
                            textAlign: 'center'
                        }}
                    >
                        {t('policy.intro')}
                    </Typography>
                    <Divider sx={{ my: { xs: 2, md: 3 }, borderColor: 'rgba(255,107,0,0.3)' }} />
                    <Typography
                        sx={{
                            fontFamily: FONT_FAMILY,
                            background: 'linear-gradient(135deg, #ff6b00, #ffd700)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '1rem', md: '1.2rem' },
                            fontWeight: 600,
                            textAlign: 'center'
                        }}
                    >
                        {t('policy.acceptance')}
                    </Typography>
                </Paper>

                {/* Policy Sections Grid */}
                <Grid container spacing={{ xs: 2, md: 4 }}>
                    {sections.map((section, index) => (
                        <Grid item xs={12} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    background: 'rgba(255,255,255,0.02)',
                                    backdropFilter: 'blur(20px)',
                                    borderRadius: { xs: 2, md: 3 },
                                    p: { xs: 2.5, sm: 3, md: 4 },
                                    border: `1px solid ${section.color}20`,
                                    boxShadow: `0 8px 32px ${section.color}15`,
                                    animation: `${fadeInUp} 0.6s ease-out ${0.1 * index}s both`,
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: `linear-gradient(135deg, ${section.color}08 0%, transparent 100%)`,
                                        opacity: 0,
                                        transition: 'opacity 0.4s ease',
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-8px) scale(1.01)',
                                        boxShadow: `0 16px 48px ${section.color}25`,
                                        border: `1px solid ${section.color}40`,
                                        '&::before': {
                                            opacity: 1
                                        },
                                        '& .icon-container': {
                                            transform: 'rotate(360deg) scale(1.1)',
                                        }
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, md: 3 }, position: 'relative', zIndex: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                                    <Box
                                        className="icon-container"
                                        sx={{
                                            background: section.gradient,
                                            color: '#fff',
                                            width: { xs: 60, sm: 70, md: 80 },
                                            height: { xs: 60, sm: 70, md: 80 },
                                            borderRadius: { xs: '15px', md: '20px' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: `0 8px 24px ${section.color}40`,
                                            transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '& svg': {
                                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                                            }
                                        }}
                                    >
                                        {section.icon}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontFamily: FONT_FAMILY,
                                                color: section.color,
                                                fontWeight: 700,
                                                mb: 3,
                                                fontSize: { xs: '1.3rem', md: '1.6rem' },
                                                textShadow: `0 0 20px ${section.color}40`
                                            }}
                                        >
                                            {t(`policy.sections.${section.key}.title`)}
                                        </Typography>
                                        <Box>
                                            {t(`policy.sections.${section.key}.content`, { returnObjects: true }).map((paragraph, pIndex) => (
                                                <Typography
                                                    key={pIndex}
                                                    sx={{
                                                        fontFamily: FONT_FAMILY,
                                                        color: paragraph.startsWith('•') || paragraph.startsWith('⏳') ? '#d5d5d5' : '#f0f0f0',
                                                        fontSize: { xs: '0.95rem', md: '1.05rem' },
                                                        lineHeight: { xs: 1.7, md: 1.9 },
                                                        mb: paragraph.startsWith('•') || paragraph.startsWith('⏳') ? { xs: 0.75, md: 1 } : { xs: 1.5, md: 2 },
                                                        pl: paragraph.startsWith('•') || paragraph.startsWith('⏳') ? { xs: 2.5, md: 3 } : 0,
                                                        position: 'relative',
                                                        '&::before': paragraph.startsWith('•') ? {
                                                            content: '""',
                                                            position: 'absolute',
                                                            left: { xs: 6, md: 8 },
                                                            top: { xs: 12, md: 14 },
                                                            width: { xs: 6, md: 8 },
                                                            height: { xs: 6, md: 8 },
                                                            borderRadius: '50%',
                                                            background: section.gradient,
                                                            boxShadow: `0 0 10px ${section.color}60`
                                                        } : {}
                                                    }}
                                                >
                                                    {paragraph.startsWith('•') ? paragraph.substring(2) : paragraph}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Footer */}
                <Paper
                    elevation={0}
                    sx={{
                        textAlign: 'center',
                        mt: { xs: 6, md: 10 },
                        pt: { xs: 4, md: 6 },
                        pb: { xs: 4, md: 6 },
                        px: { xs: 2, md: 0 },
                        background: 'rgba(255,255,255,0.02)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: { xs: 3, md: 4 },
                        border: '1px solid rgba(255,107,0,0.2)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #ff6b00, #ff9f4a, #ffd700, #ff9f4a, #ff6b00)',
                            backgroundSize: '200% 100%',
                            animation: 'gradient-shift 3s ease infinite',
                        }
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            fontFamily: FONT_FAMILY,
                            background: 'linear-gradient(135deg, #fff 0%, #ff9f4a 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 800,
                            mb: 2,
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            letterSpacing: '2px'
                        }}
                    >
                        SHISHA VIBES
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontFamily: FONT_FAMILY,
                            background: 'linear-gradient(135deg, #ff6b00, #ffd700)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontStyle: 'italic',
                            fontSize: { xs: '1.2rem', md: '1.5rem' },
                            fontWeight: 600
                        }}
                    >
                        {t('policy.footer')}
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
}
